/**
 * shared/pmove/pm.ts — the per-call pmove scratch (`pmove_t` + `pml_t`), trace helpers, angle
 * helpers and the pmove-private constants.
 *
 * OWNER: PMOVE agent.
 *
 * Source map (see research/pmove-t6mp.md = [PM §n], research/movement-public.md = [MOVPUB §n]):
 *  - The T6 decompile [PM] gives the structure and the hard-coded constants.
 *  - Where the T6 pseudocode is condensed, the code follows the KisakBlack T5 MP reimplementation
 *    (research/sources/movement/kisakblack-t5/, cited as [T5 file:function]) which T6 is a direct
 *    evolution of [MOVPUB §0]. Every such value is cross-checked against [PM §4] where it exists.
 *
 * Nothing in here survives a pmove() call except what is written into the PlayerState.
 */
import { ENTITYNUM_NONE, MAX_CLIENTS } from '../constants';
import {
  CONTENTS, MASK_PLAYERSOLID, createTraceResult, type TraceResult,
} from '../collision/types';
import { DEG2RAD, RAD2DEG, datan, datan2, dcos, dsin, type ReadonlyVec3, type Vec3 } from '../math';
import { createUserCmd, type PlayerState, type UserCmd } from '../types';
import type { PmoveContext } from './types';

// =============================================================================================
// pmove_t + pml_t
// =============================================================================================
/** T5/T6 `pml_t` (per-PmoveSingle locals) merged with the per-call parts of `pmove_t`. */
export interface Pm {
  ps: PlayerState;
  /** pm->cmd: a per-chunk COPY of the usercmd that pmove may edit (button masking, sprint strafe). */
  cmd: UserCmd;
  /** pm->oldcmd.buttons: the previous usercmd's buttons as sent (ps.oldButtons, ARCHITECTURE §9.2). */
  oldButtons: number;
  ctx: PmoveContext;
  /** pm->mins / pm->maxs: the hull for this frame (stance dependent). */
  mins: Vec3;
  maxs: Vec3;
  /** pm->tracemask. */
  tracemask: number;
  /** pm->xyspeed (Footsteps / step bob). */
  xyspeed: number;
  // ---- pml_t ----
  forward: Vec3;
  right: Vec3;
  up: Vec3;
  frametime: number;
  msec: number;
  walking: boolean;
  groundPlane: boolean;
  almostGroundPlane: boolean;
  groundTrace: TraceResult;
  impactSpeed: number;
  previousOrigin: Vec3;
  previousVelocity: Vec3;
}

export function createPm(ctx: PmoveContext, ps: PlayerState): Pm {
  return {
    ps, cmd: createUserCmd(), oldButtons: 0, ctx,
    mins: [-15, -15, 0], maxs: [15, 15, 70], tracemask: MASK_PLAYERSOLID, xyspeed: 0,
    forward: [0, 0, 0], right: [0, 0, 0], up: [0, 0, 0],
    frametime: 0, msec: 0, walking: false, groundPlane: false, almostGroundPlane: false,
    groundTrace: createTraceResult(), impactSpeed: 0,
    previousOrigin: [0, 0, 0], previousVelocity: [0, 0, 0],
  };
}

/** memset(&pml, 0) at the top of PmoveSingle [T5 bg_pmove.cpp:PmoveSingle]. */
export function resetPml(pm: Pm): void {
  pm.forward[0] = pm.forward[1] = pm.forward[2] = 0;
  pm.right[0] = pm.right[1] = pm.right[2] = 0;
  pm.up[0] = pm.up[1] = pm.up[2] = 0;
  pm.frametime = 0;
  pm.msec = 0;
  pm.walking = false;
  pm.groundPlane = false;
  pm.almostGroundPlane = false;
  clearTrace(pm.groundTrace);
  pm.impactSpeed = 0;
}

export function clearTrace(t: TraceResult): void {
  t.fraction = 1; t.startsolid = false; t.allsolid = false;
  t.normal[0] = t.normal[1] = t.normal[2] = 0;
  t.endpos[0] = t.endpos[1] = t.endpos[2] = 0;
  t.planeDist = 0; t.contents = 0; t.surfaceFlags = 0; t.surfaceType = 0; t.entityNum = ENTITYNUM_NONE;
}

export function copyTrace(dst: TraceResult, src: TraceResult): void {
  dst.fraction = src.fraction; dst.startsolid = src.startsolid; dst.allsolid = src.allsolid;
  dst.normal[0] = src.normal[0]; dst.normal[1] = src.normal[1]; dst.normal[2] = src.normal[2];
  dst.endpos[0] = src.endpos[0]; dst.endpos[1] = src.endpos[1]; dst.endpos[2] = src.endpos[2];
  dst.planeDist = src.planeDist; dst.contents = src.contents; dst.surfaceFlags = src.surfaceFlags;
  dst.surfaceType = src.surfaceType; dst.entityNum = src.entityNum;
}

// =============================================================================================
// Traces
// =============================================================================================
/**
 * Stance / mantle / prone checks trace with `pm->tracemask & 0xFDFF7FFF` [T5 PM_CanStand,
 * PM_CheckDuck]: 0x02000000 is CONTENTS_BODY (same bit as ours), i.e. other players never stop
 * you from standing up or going prone.
 */
export const STANCE_MASK_CLEAR = ~(CONTENTS.BODY | 0x8000);

/** PM_playerTrace: hull trace for the moving player (skips itself). */
export function pmTrace(
  pm: Pm, out: TraceResult, start: ReadonlyVec3, mins: ReadonlyVec3, maxs: ReadonlyVec3, end: ReadonlyVec3,
  mask: number = pm.tracemask,
): TraceResult {
  return pm.ctx.trace(out, start, end, mins, maxs, mask, pm.ps.clientNum);
}

/**
 * trace.walkable: the T5 collision code sets it for normal.z >= 0.7 [MOVPUB §1.4; T5 cm_trace.cpp:458
 * `walkable = normal[2] >= 0.69999999`]. The T6 GroundTrace threshold is unverified [PM §9] ->
 * ctx.dvars.minWalkNormal (ESTIMATE 0.7).
 */
export function traceWalkable(pm: Pm, t: TraceResult): boolean {
  return t.fraction < 1 && !t.allsolid && t.normal[2] >= pm.ctx.dvars.minWalkNormal;
}

/** Trace_GetEntityHitId(&trace) < com_maxclients [T5 PM_StepSlideMove]. */
export function traceHitClient(t: TraceResult): boolean {
  return t.fraction < 1 && t.entityNum >= 0 && t.entityNum < MAX_CLIENTS;
}

/** PM_AddTouchEnt [T5 bg_pmove.cpp:315]: unique list of entities touched this pmove. */
export function addTouchEnt(pm: Pm, entityNum: number): void {
  if (entityNum === ENTITYNUM_NONE) return;
  const list = pm.ctx.touchEnts;
  for (let i = 0; i < list.length; i++) if (list[i] === entityNum) return;
  if (list.length < 32) list.push(entityNum);
}

// =============================================================================================
// Angles / vectors (deterministic, full float precision — IW AngleNormalize*, AngleDelta)
// =============================================================================================
export function angleNorm360(a: number): number {
  return a - 360 * Math.floor(a * (1 / 360));
}
export function angleNorm180(a: number): number {
  const x = angleNorm360(a);
  return x > 180 ? x - 360 : x;
}
/** IW AngleDelta(a1, a2) = AngleNormalize180(a1 - a2). */
export function angleDelta(a1: number, a2: number): number {
  return angleNorm180(a1 - a2);
}
/** vectoyaw: yaw of v in [0, 360). */
export function vecToYaw(v: ReadonlyVec3): number {
  if (v[0] === 0 && v[1] === 0) return 0;
  let yaw = datan2(v[1], v[0]) * RAD2DEG;
  if (yaw < 0) yaw += 360;
  return yaw;
}
/** vectopitch (IW): positive = looking down. */
export function vecToPitch(v: ReadonlyVec3): number {
  if (v[0] === 0 && v[1] === 0) return v[2] > 0 ? -90 : 90;
  const f = Math.sqrt(v[0] * v[0] + v[1] * v[1]);
  return -datan2(v[2], f) * RAD2DEG;
}
/** YawVectors2D: (cos yaw, sin yaw). */
export function yawVectors2D(yaw: number, out: Vec3): Vec3 {
  const r = yaw * DEG2RAD;
  out[0] = dcos(r); out[1] = dsin(r); out[2] = 0;
  return out;
}
/**
 * PitchForYawOnNormal (IW; body not in the T5 sources): the pitch of the direction `yaw` laid
 * onto the plane `normal` (positive = facing downhill). ESTIMATE reconstruction.
 */
export function pitchForYawOnNormal(yaw: number, normal: ReadonlyVec3): number {
  if (normal[2] === 0) return 0;
  const r = yaw * DEG2RAD;
  const fz = (normal[0] * dcos(r) + normal[1] * dsin(r)) / normal[2];
  return datan(fz) * RAD2DEG;
}
/** Vec2Normalize in place (z untouched), returns the length. */
export function vec2Normalize(v: Vec3): number {
  const len = Math.sqrt(v[0] * v[0] + v[1] * v[1]);
  if (len) { const il = 1 / len; v[0] *= il; v[1] *= il; }
  return len;
}

// =============================================================================================
// Effective stance (T6 internal order: 0 stand, 1 prone, 2 crouch) [PM §3 PM_GetEffectiveStance]
// =============================================================================================
export const PM_STANCE_STAND = 0;
export const PM_STANCE_PRONE = 1;
export const PM_STANCE_CROUCH = 2;

/** PM_GetEffectiveStance (0x4c2e50): viewHeightTarget 11 -> prone, 40 -> crouch, else stand. */
export function effectiveStance(ps: PlayerState): number {
  if (ps.viewHeightTarget === 40) return PM_STANCE_CROUCH;
  return ps.viewHeightTarget === 11 ? PM_STANCE_PRONE : PM_STANCE_STAND;
}

// =============================================================================================
// pmove-private ESTIMATE constants (hidden T6 dvars that are not part of PmoveDvars).
// T5 values from research/sources/movement/kisakblack-t5/bg_misc.cpp unless noted. They are
// compile-time constants, so server and client always agree.
// =============================================================================================
/** player_moveThreshhold 2 [T5 bg_misc.cpp:1102] (T6 hidden 0x322e7f4, PM §6) ESTIMATE. */
export const PM_MOVE_THRESHOLD = 2;
/** player_footstepsThreshhold 10 [T5 bg_misc.cpp:1110] (T6 hidden 0x322e840) ESTIMATE. */
export const PM_FOOTSTEPS_THRESHOLD = 10;
/** player_runThreshhold 110 [T5 bg_misc.cpp:1117] (T6 hidden 0x322c610) ESTIMATE. */
export const PM_RUN_THRESHOLD = 110;
/** player_runbkThreshhold 60 [T5 bg_misc.cpp:1095] ESTIMATE. */
export const PM_RUNBK_THRESHOLD = 60;
/** player_sprintThreshhold 185 [T5 bg_misc.cpp:1131] (T6 hidden 0x322c748) ESTIMATE. */
export const PM_SPRINT_THRESHOLD = 185;
/** player_sprintCameraBob 0.5 [MOVPUB §1.2; T5 bg_misc.cpp:1267] (T6 hidden 0x322ea48) ESTIMATE. */
export const PM_SPRINT_CAMERA_BOB = 0.5;
/** bg_prone_yawcap 45 [T5 bg_misc.cpp:645] ESTIMATE. */
export const PM_PRONE_YAWCAP = 45;
/** bg_ladder_yawcap 100 [T5 bg_misc.cpp:638] ESTIMATE. */
export const PM_LADDER_YAWCAP = 100;
/** Prone body turn rate 55 deg/s [T5 PM_UpdateViewAngles_Prone `55.0 * msec * 0.001`] ESTIMATE. */
export const PM_PRONE_TURN_RATE = 55;
/** Prone pitch adaption 70 deg/s [T5 PM_UpdatePronePitch; PM §2 0x64c9b0 "70 deg/s"]. */
export const PM_PRONE_PITCH_RATE = 70;
/** PRONE_FEET_DIST_TURNED 55 / default feet dist 50 / PRONE_WAIST_DIST 12 [T5 bg_pmove.cpp:21, bg_misc.cpp:2989]. */
export const PRONE_FEET_DIST = 50;
export const PRONE_FEET_DIST_TURNED = 55;
export const PRONE_WAIST_DIST = 12;
/** player_meleeRange 64 [T5 bg_misc.cpp:1521; WPN §5] ESTIMATE for T6. */
export const PM_MELEE_RANGE = 64;
/** Ladder friction-ish side damping 16 [T5 pm_ladderfriction / PM_LadderMove `* 16.0`]. */
export const PM_LADDER_SIDE_FRICTION = 16;
/** Dive to prone. BO2 MP is widely reported to have no dolphin dive [MOVPUB §1.4 ESTIMATE];
 *  the T6 exe still registers dtp_* (dvars_t6mp.json). Off by default; see dtp.ts. */
export const PM_DTP_ENABLED = false;
